import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Redirect, useHistory } from 'react-router-dom'
import config from '../../config'
import { loginUser } from '../../ducks/auth'
import routes from '../../routes'
import { ForgetPassword } from './ForgetPassword'
import styled, { css } from 'styled-components'
import { desktopAssets, mobileAssets } from 'src/containers/PreCache'
import Media from 'react-media'
import { SIZES } from 'src/styles/theme'
import Amplitude from 'src/utils/amplitudeHelper'
import { ReactComponent as CheckIcon } from 'src/images/ntc/login/checkbox-check.svg'

export const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agree, setAgree] = useState(false)

  const { isFetching, isAuthenticated } = useSelector((state) => state.auth)
  const history = useHistory()
  const dispatch = useDispatch()

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    loginUser(dispatch, history, {
      email,
      password,
    })
  }

  const onPasswordInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value.toUpperCase())
  }

  const onEmailInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.toLowerCase())
  }

  const onAgreeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgree(e.target.checked)
  }

  useEffect(() => {
    document.title = `${config.eventName} | Login`
  }, [])

  const showHelpDesk = () => {
    // Guard: window.chaport is undefined while the widget script is disabled
    // (see public/index.html / config.ENABLE_CHAPORT).
    const cpt = window.chaport
    if (!cpt) return
    cpt.open()
    Amplitude.clickHelpDesk()
  }

  if (isAuthenticated) {
    return <Redirect to={routes.lobby} />
  }

  const isSubmitDisabled = !(email.length && password.length && agree) || isFetching

  const loginForm = (
    <form onSubmit={handleLogin} className="login-form">
      <div className="fields">
        <div className="input-group">
          <input
            className="form-input"
            type="email"
            placeholder="Email address"
            autoComplete="email"
            value={email}
            onChange={onEmailInput}
          />
        </div>
        <div className="input-group">
          <input
            className="form-input"
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={onPasswordInput}
          />
        </div>
        <div className="links-row">
          <ForgetPassword />
          <div className="assistance-link">
            <span>Need Assistance? </span>
            <span role="button" className="click-here" onClick={showHelpDesk}>
              Click Here
            </span>
          </div>
        </div>
      </div>

      <div className="agree-section">
        <label className="agree-row">
          <input id="agree" type="checkbox" checked={agree} onChange={onAgreeInput} />
          <span className="checkbox-visual" aria-hidden="true">
            <CheckIcon />
          </span>
          <span className="desc">
            I hereby acknowledge that I have read and understood this{' '}
            <Link to="/Privacy Notice for Participants.pdf" target="_blank">
              Personal Data Protection Privacy Notice.
            </Link>{' '}
            By checking this box, I acknowledge agreeing to the registration terms and conditions as well as consent to the
            processing of my personal data in accordance with the terms of this privacy notice.
          </span>
        </label>

        <button type="submit" className="submit-btn" disabled={isSubmitDisabled} onClick={handleLogin}>
          {isFetching ? 'Loading...' : 'Log In'}
        </button>
      </div>
    </form>
  )

  return (
    <Media query={`(max-width: ${SIZES.mobileBreakpoint})`}>
      {(matches) =>
        matches ? (
          <MobilePage>
            <div className="hero">
              <div className="event-content" aria-label="National Tax Conference 2026">
                <img
                  className="mobile-title-lockup"
                  src={mobileAssets.countdownMobileTitle}
                  alt="National Tax Conference 2026 — 26th Edition. Future-Ready Tax: Driving Trust, Transparency and Transformation"
                />
              </div>
            </div>
            <div className="login-card">
              <h2 className="login-title">Log in</h2>
              {loginForm}
            </div>
          </MobilePage>
        ) : (
          <DesktopPage>
            <div className="hero">
              <div className="hero-content">
                <img
                  className="title-lockup"
                  src={desktopAssets.countdownTitle}
                  alt="National Tax Conference 2026 — 26th Edition. Future-Ready Tax: Driving Trust, Transparency and Transformation"
                />
              </div>
              <div className="login-card">
                <h2 className="login-title">Log in</h2>
                {loginForm}
              </div>
            </div>
          </DesktopPage>
        )
      }
    </Media>
  )
}

/**
 * Shared styles for the login form contents (fields, links row, checkbox and
 * submit button). Reused by both the desktop and mobile wrappers so the two
 * breakpoints stay visually consistent while allowing each wrapper to control
 * outer positioning/sizing independently.
 */
const loginFormStyles = css`
  .login-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 19px;
  }

  .fields {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 22px;
  }

  .input-group {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-input {
    width: 100%;
    height: 48px;
    padding: 14px 16px;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    background: #fff;
    text-align: center;
    font-family: 'Din', 'DIN', sans-serif;
    font-size: 18px;
    color: #374151;
    appearance: none;

    &::placeholder {
      color: #6b7280;
      font-family: 'Din', 'DIN', sans-serif;
      font-size: 18px;
    }

    &:focus {
      outline: none;
      border-color: #262c65;
    }
  }

  .links-row {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 10px 48px;
    font-family: 'Din', 'DIN', sans-serif;
    font-size: 14px;
    text-align: center;
  }

  .assistance-link {
    color: #374151;
    white-space: nowrap;
  }

  .click-here {
    font-weight: 700;
    color: #262c65;
    text-decoration: underline;
    cursor: pointer;
  }

  .agree-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  .agree-row {
    position: relative;
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    cursor: pointer;
    margin: 0;

    input[type='checkbox'] {
      position: absolute;
      top: 0;
      left: 0;
      width: 15px;
      height: 15px;
      margin: 0;
      opacity: 0;
      cursor: pointer;
    }

    .checkbox-visual {
      flex-shrink: 0;
      width: 15px;
      height: 15px;
      margin-top: 1px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 2px;
      border: 1px solid #d1d5db;
      background: #fff;
      transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;

      svg {
        width: 10px;
        height: 7.5px;
        opacity: 0;
      }
    }

    input[type='checkbox']:checked ~ .checkbox-visual {
      background: #0eb41f;
      border-color: #0eb41f;

      svg {
        opacity: 1;
      }
    }

    input[type='checkbox']:focus-visible ~ .checkbox-visual {
      outline: 2px solid #262c65;
      outline-offset: 2px;
    }

    .desc {
      flex: 1;
      font-family: 'Din', 'DIN', sans-serif;
      font-size: 12px;
      line-height: 1.4;
      color: #374151;
      text-align: left;

      a {
        color: #374151;
        text-decoration: underline;
      }
    }
  }

  .submit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 16px 40px;
    border: none;
    border-radius: 10px;
    background: var(--Gold-Gradient-01);
    color: #fff;
    font-family: 'Din', 'DIN', sans-serif;
    font-weight: 700;
    font-size: 26px;
    text-transform: uppercase;
    box-shadow: 0px 4px 6.15px rgba(0, 0, 0, 0.21);
    cursor: pointer;
    transition: transform 0.15s ease-in-out, opacity 0.15s ease-in-out;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`

/*
 * Desktop hero is built from Figma node 9231:162, a fixed 1920x1080 canvas.
 * ".hero" is locked to that same 16:9 aspect ratio (instead of min-height:100vh)
 * so every child's percentage-based position/size maps 1:1 to the Figma
 * coordinates no matter the viewport width. The outer page only provides a
 * full-viewport fallback background in case the viewport is taller than the
 * hero's natural (aspect-ratio-derived) height.
 *
 * IMPORTANT — why .hero-content and .login-card's numbers are NOT a 1:1
 * translation of the Figma frame (title w:890 / card top:52.963%): Figma's
 * "Countdown Timer" card frame (h:439) undershoots the card's real rendered
 * height once live copy/fonts are laid out (~467px even at the full 602px
 * design width, more once the card narrows below that). At the Figma-exact
 * proportions, ".hero"'s aspect-ratio-derived height shrinks faster (it's
 * pure vw) than the card's largely-fixed-px content does, so on any desktop
 * viewport narrower than ~1740px (i.e. almost every real browser window,
 * including common 1440-wide laptops) the card's bottom edge fell below
 * ".hero"'s own bottom edge and forced a page scroll just to see the submit
 * button. The title lockup is scaled down from Figma (890px -> 650px cap,
 * see .hero-content) and .login-card's top offset is pulled up from 52.963%
 * to 41% so the fixed-height card has enough room above ".hero"'s bottom
 * edge at 1440x816-1920x1080. Re-check with the fit-check math below if
 * either the card's copy or the title lockup asset changes.
 */
const DesktopPage = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
  font-family: 'Din', 'DIN', sans-serif;

  .hero {
    position: relative;
    width: 100%;
    min-height: 100vh;
    background: url(${desktopAssets.loginHeroBg}) center / cover no-repeat;
  }

  /* The title artwork includes the organizer logos, event lockup and date.
     Sized/positioned off the "Title" instance in Figma but scaled down
     (890px -> 650px cap) so the block occupies less of ".hero"'s height,
     leaving room for .login-card underneath — see the fit-check comment
     above. ".hero" height is strictly derived from its width via
     aspect-ratio, so every value below is a pure min(designMaxPx, vw%) —
     i.e. it always scales in lockstep with the hero's own height.
     Deliberately NOT using a clamp() with a px floor here: a floor decouples
     this block's size from the hero's height once the vw value drops below
     it, so the title/pill stop shrinking while the hero (and the
     .login-card's % top position) keep shrinking underneath them — closing
     the gap and causing the login-card to overlap the title on narrower
     desktop widths (e.g. ~820-1200px browser windows, still above the
     mobile breakpoint). */
  .hero-content {
    position: absolute;
    left: 4.1667%;
    top: 6.5741%;
    width: min(650px, 34vw);
    display: flex;
    flex-direction: column;
  }

  .title-lockup {
    display: block;
    width: 100%;
    height: auto;
  }

  /* Horizontally matches the "Countdown Timer" (login card) frame in Figma:
     x:158 w:602 on the 1920x1080 canvas. Vertically, "top" is 41% rather
     than Figma's 52.963% (572/1080) and "width"'s vw coefficient is bumped
     from Figma's 31.35vw to 41.8vw (full 602px width by ~1440px viewports
     instead of only at 1920px) — both purely for the viewport-fit reasons
     documented above .hero-content; see that comment before changing either
     value. */
  .login-card {
    position: absolute;
    left: 8.229%;
    top: 47%;
    width: clamp(380px, 41.8vw, 602px);
    padding: 26px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 17px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 238, 189, 0.75) 100%);
    box-shadow: 0 4px 26px rgba(0, 0, 0, 0.26);
    backdrop-filter: blur(13.75px);
    -webkit-backdrop-filter: blur(13.75px);
  }

  .login-title {
    width: 100%;
    margin: 0;
    font-weight: 700;
    font-size: 30px;
    text-transform: uppercase;
    text-align: center;
    color: #262c65;
  }

  ${loginFormStyles}

  @media (max-width: 1150px) {
    .login-card {
      padding: 22px;
      gap: 16px;
    }

    .login-title {
      font-size: 24px;
    }

    .submit-btn {
      padding: 12px 32px;
      font-size: 20px;
    }
  }
`

const MobilePage = styled.div`
  width: 100%;
  min-height: 100dvh;
  padding-bottom: 40px;
  background: #000b23 url(${mobileAssets.loginHeroBg}) center top / cover no-repeat;
  font-family: 'Din', 'DIN', sans-serif;

  .hero {
    position: relative;
    width: 100%;
    min-height: 480px;
    aspect-ratio: 1572 / 2100;
  }

  .event-content {
    width: min(337px, calc(100% - 40px));
    position: absolute;
    top: 54px;
    left: 20px;
  }

  .mobile-title-lockup {
    width: 100%;
    height: auto;
    display: block;
  }

  .login-card {
    position: relative;
    margin: -24px 16px 0;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 238, 189, 0.75) 100%);
    box-shadow: 0 4px 26px rgba(0, 0, 0, 0.26);
    backdrop-filter: blur(4.7px);
    -webkit-backdrop-filter: blur(4.7px);
  }

  .login-title {
    width: 100%;
    margin: 0;
    font-weight: 700;
    font-size: 22px;
    text-transform: uppercase;
    text-align: center;
    color: #262c65;
  }

  ${loginFormStyles}

  .form-input {
    font-size: 16px;

    &::placeholder {
      font-size: 16px;
    }
  }

  .links-row {
    font-size: 14px;
    gap: 8px 20px;
  }

  .submit-btn {
    width: 100%;
    font-size: 18px;
    padding: 14px 24px;
  }

  @media (max-width: 360px) {
    .event-content {
      width: calc(100% - 32px);
      left: 16px;
    }

    .login-card {
      margin-inline: 12px;
    }
  }
`
